(() => {
  'use strict';

  const config = window.SignalMile || {};
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const liveRegion = () => document.getElementById('SignalMileLiveRegion');

  function announce(message) {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  function publish(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(`signal-mile:${name}`, { detail }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `signal_mile_${name}`, ...detail });
  }

  function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
    const value = Number(cents || 0) / 100;
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatted.replace(/,/g, ' ').replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, formatted);
  }

  class DrawerController {
    constructor(element) {
      this.element = element;
      this.panel = element.querySelector('.drawer__panel');
      this.trigger = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.element.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
    }

    open(trigger) {
      if (!this.panel) return;
      document.querySelectorAll('[data-drawer][aria-hidden="false"]').forEach((drawer) => {
        if (drawer !== this.element) drawer.drawerController?.close(false);
      });
      this.trigger = trigger || document.activeElement;
      this.element.setAttribute('aria-hidden', 'false');
      this.element.classList.add('is-open');
      document.body.classList.add('drawer-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.boundKeydown);
      window.requestAnimationFrame(() => {
        const preferred = this.element.querySelector('[data-predictive-search-input]');
        const first = preferred || this.panel.querySelector(focusableSelector);
        first?.focus();
      });
    }

    close(restoreFocus = true) {
      this.element.setAttribute('aria-hidden', 'true');
      this.element.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      document.removeEventListener('keydown', this.boundKeydown);
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.trigger?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll(focusableSelector)].filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function initializeDrawers(scope = document) {
    scope.querySelectorAll('[data-drawer]').forEach((drawer) => {
      if (drawer.drawerController) return;
      drawer.drawerController = new DrawerController(drawer);
    });
  }

  function openDrawer(id, trigger) {
    const drawer = document.getElementById(id);
    drawer?.drawerController?.open(trigger);
  }

  async function renderCart({ open = false } = {}) {
    const url = new URL(config.routes.root || '/', window.location.origin);
    url.searchParams.set('section_id', 'cart-drawer');
    const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(config.strings?.cartError || 'Cart could not be rendered.');
    const html = await response.text();
    if (!html.trim()) throw new Error(config.strings?.cartError || 'Cart could not be rendered.');

    const parser = new DOMParser();
    const nextDocument = parser.parseFromString(html, 'text/html');
    const nextSection = nextDocument.querySelector('#shopify-section-cart-drawer');
    const currentSection = document.querySelector('#shopify-section-cart-drawer');

    if (nextSection && currentSection) {
      currentSection.replaceWith(nextSection);
    } else if (currentSection) {
      currentSection.innerHTML = html;
    }

    initializeDrawers(document);
    const cartResponse = await fetch(`${config.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });
    if (open) openDrawer('CartDrawer', document.querySelector('[data-cart-open]'));
    publish('cart-updated', { item_count: cart.item_count, total_price: cart.total_price });
    return cart;
  }

  async function addToCart(form, submitButton) {
    const formData = new FormData(form);
    submitButton.disabled = true;
    submitButton.classList.add('is-loading');
    const originalLabel = submitButton.textContent;
    submitButton.textContent = config.strings?.adding || 'Adding';

    try {
      const response = await fetch(config.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || config.strings?.cartError);
      submitButton.textContent = config.strings?.added || 'Added';
      announce(`${item.product_title || 'Item'} added to bag.`);
      publish('add-to-cart', {
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        value: item.final_price
      });
      window.location.assign(config.routes.cart);
      return;
    } catch (error) {
      announce(error.message || config.strings?.cartError);
      const errorBox = form.querySelector('[data-product-form-error]');
      if (errorBox) {
        errorBox.textContent = error.message || config.strings?.cartError;
        errorBox.hidden = false;
      }
      submitButton.textContent = originalLabel;
    } finally {
      submitButton.disabled = false;
      submitButton.classList.remove('is-loading');
    }
  }

  function updateVariant(form) {
    const jsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!jsonNode) return;

    let product;
    try {
      product = JSON.parse(jsonNode.textContent);
    } catch (_) {
      return;
    }

    const selectedOptions = [...form.querySelectorAll('[data-option-position]')]
      .filter((input) => input.checked)
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);

    const variant = product.variants.find((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]));
    const idInput = form.querySelector('[name="id"]');
    const addButton = form.querySelector('[name="add"]');
    const section = form.closest('[data-product-section]');
    const stickyButton = section?.querySelector('[data-sticky-add]');
    const price = section?.querySelector('[data-price]');
    const availability = section?.querySelector('[data-variant-availability]');

    if (!variant) {
      if (idInput) idInput.value = '';
      [addButton, stickyButton].forEach((button) => {
        if (!button) return;
        button.disabled = true;
        button.textContent = config.strings?.unavailable || 'Unavailable';
      });
      if (availability) availability.textContent = config.strings?.unavailable || 'Unavailable';
      return;
    }

    if (idInput) idInput.value = variant.id;
    const label = variant.available ? config.strings?.addToCart || 'Add to bag' : config.strings?.soldOut || 'Sold out';
    [addButton, stickyButton].forEach((button) => {
      if (!button) return;
      button.disabled = !variant.available;
      button.textContent = label;
    });
    if (availability) availability.textContent = variant.available ? 'Made to order / ships in 2–7 business days' : label;
    if (price) {
      const current = price.querySelector('.price__current');
      const compare = price.querySelector('.price__compare');
      if (current) current.textContent = formatMoney(variant.price);
      if (compare) {
        compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !(variant.compare_at_price > variant.price);
      }
    }
    if (variant.featured_media?.id) {
      const media = section?.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      media?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    publish('variant-selected', { product_id: product.id, variant_id: variant.id, available: variant.available });
  }

  function initializeProductForms(scope = document) {
    scope.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      form.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-position]')) updateVariant(form);
      });
      form.addEventListener('submit', (event) => {
        const submitButton = event.submitter;
        if (!submitButton || submitButton.name !== 'add') return;
        event.preventDefault();
        addToCart(form, submitButton);
      });
    });
  }

  function initializePredictiveSearch(scope = document) {
    scope.querySelectorAll('[data-predictive-search-input]').forEach((input) => {
      if (input.dataset.initialized) return;
      input.dataset.initialized = 'true';
      let timer;
      let controller;
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        controller?.abort();
        const query = input.value.trim();
        const results = input.closest('.drawer__panel')?.querySelector('[data-predictive-search-results]');
        if (!results || query.length < 2) {
          if (results) results.innerHTML = '';
          return;
        }
        timer = window.setTimeout(async () => {
          controller = new AbortController();
          results.setAttribute('aria-busy', 'true');
          const params = new URLSearchParams({
            q: query,
            'resources[type]': 'product,collection,page',
            'resources[limit]': '6',
            'resources[options][unavailable_products]': 'last',
            section_id: 'predictive-search'
          });
          try {
            const response = await fetch(`${config.routes.predictiveSearch}?${params}`, { signal: controller.signal });
            if (!response.ok) throw new Error('Search unavailable');
            const text = await response.text();
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            results.innerHTML = parsed.querySelector('#shopify-section-predictive-search')?.innerHTML || text;
          } catch (error) {
            if (error.name !== 'AbortError') results.innerHTML = '<p class="form-status">Search is temporarily unavailable. Submit to see full results.</p>';
          } finally {
            results.removeAttribute('aria-busy');
          }
        }, 180);
      });
    });
  }

  function initializeRecommendations(scope = document) {
    scope.querySelectorAll('[data-product-recommendations]').forEach((section) => {
      if (section.dataset.initialized || !section.dataset.url) return;
      section.dataset.initialized = 'true';
      const load = async () => {
        try {
          const response = await fetch(section.dataset.url, { headers: { Accept: 'text/html' } });
          if (!response.ok) return;
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = parsed.querySelector('[data-product-recommendations]');
          if (next?.innerHTML.trim()) {
            section.innerHTML = next.innerHTML;
            initializeProductForms(section);
          }
        } catch (_) {
          // Recommendations are non-critical; the purchase path remains available.
        }
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        }, { rootMargin: '400px 0px' });
        observer.observe(section);
      } else {
        load();
      }
    });
  }

  async function changeCartLine(line, quantity, control) {
    control?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(config.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || config.strings?.cartError);
      await renderCart({ open: true });
      announce(quantity === 0 ? 'Item removed from bag.' : 'Bag quantity updated.');
    } catch (error) {
      announce(error.message || config.strings?.cartError);
    } finally {
      control?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.dataset.drawerOpen, opener);
      return;
    }

    const cartOpener = event.target.closest('[data-cart-open]');
    if (cartOpener) {
      event.preventDefault();
      if (config.cartType === 'page') {
        window.location.assign(config.routes.cart);
      } else {
        openDrawer('CartDrawer', cartOpener);
      }
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const line = quantityButton.closest('[data-cart-line]');
      const input = line?.querySelector('[data-cart-quantity]');
      if (!line || !input) return;
      const quantity = Math.max(0, Number(input.value) + Number(quantityButton.dataset.quantityChange));
      changeCartLine(Number(line.dataset.cartLine), quantity, line);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      const line = removeButton.closest('[data-cart-line]');
      if (line) changeCartLine(Number(line.dataset.cartLine), 0, line);
      return;
    }

    const stickyAdd = event.target.closest('[data-sticky-add]');
    if (stickyAdd) {
      const form = stickyAdd.closest('[data-product-section]')?.querySelector('[data-product-form]');
      form?.requestSubmit(form.querySelector('[name="add"]'));
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-cart-quantity]')) {
      const line = event.target.closest('[data-cart-line]');
      if (line) changeCartLine(Number(line.dataset.cartLine), Math.max(0, Number(event.target.value)), line);
    }
    if (event.target.matches('[data-sort-by]')) event.target.form?.submit();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeDrawers(event.target);
    initializeProductForms(event.target);
    initializePredictiveSearch(event.target);
    initializeRecommendations(event.target);
  });

  initializeDrawers();
  initializeProductForms();
  initializePredictiveSearch();
  initializeRecommendations();
})();
